import { isAfter, parseISO } from 'date-fns';

/**
 * Walk back from the end of the segment array to find the last current-term segment, skipping
 * renewal segments (whose startDate is past policy.endDate). There can be more than one renewal
 * segment when multiple renewal-effective changes are queued, so we cannot just subtract a fixed count.
 *
 * This mirrors `lastCurrentTermSegment` in staff-appsync-be's preview-policy.event.ts — the exact
 * rule the backend used when it originally captured the before/after ClarionDoor request ids for the
 * preview diff. Reusing it keeps the version-vs-version segment selection consistent with that capture.
 */
const lastCurrentTermSegment = (policy) => {
  const segments = policy?.segments ?? [];
  if (!segments.length) {
    return undefined;
  }
  const policyEndDate = policy?.endDate ? parseISO(policy.endDate) : undefined;
  if (policyEndDate) {
    for (let i = segments.length - 1; i >= 0; i -= 1) {
      const segment = segments[i];
      if (segment?.startDate && !isAfter(parseISO(segment.startDate), policyEndDate)) {
        return segment;
      }
    }
  }
  return segments[segments.length - 1];
};

/**
 * Extract the ClarionDoor request id for a historical policy snapshot (from getPolicyFromVersion).
 * Returns the scalar `clarionDoorId` off the last current-term segment.
 *
 * @param {object} policy - full policy snapshot for a version
 * @returns {string | undefined} the segment's clarionDoorId, or undefined if none on file
 */
export const getClarionDoorIdFromSnapshot = (policy) => lastCurrentTermSegment(policy)?.clarionDoorId;
